import React, { useMemo } from "react";
import AuthProvider from "../../auth/AuthProvider";
import JamezzFormV2 from "../JamezzForm/JamezzFormV2";
import TranslationWrapper, { getTranslation } from "../../components/TranslationWrapper/TranslationWrapper";
import { connect } from "react-redux";
import CurrencyProvider from "../../components/Forms/CurrencyProvider";
import useVestigingRelation from "../../components/Forms/useVestigingRelation";
import VestigingProvider from "../../components/Forms/VestigingProvider";
import Algemeen from "./Tabs/Algemeen";
import Zones from "./Tabs/Zones";
import Tags from "./Tabs/Tags";
import Vrijeopties from "./Tabs/Vrijeopties";
import Branding from "./Tabs/Branding";
import Informatie from "./Tabs/Informatie";
import Allergenen from "./Tabs/Allergenen";
import Translations from "./Tabs/Translations";
import Combinatievoordeel from "./Tabs/Combinatievoordeel";
import TimeTable from "./Tabs/ProductTimeTable";
import Opties from "./Tabs/Opties";
import Apidata from "./Tabs/Apidata";
import Prices from "./Tabs/Prices";
import Ayce from "./Tabs/Ayce";
import { formatTitle } from "../../../utils/formatting";
import { Link } from "react-router";
import parseJSONTryCatched from "../../Helpers/JSONTryCatched";
import ChildOptionGroups from "./Tabs/ChildOptionGroups";
import Proppos from "./Tabs/Proppos";
import Piggy from "./Tabs/Piggy";
import Pause from "./Tabs/Pause";
import DetailedDescription from "./Tabs/DetailedDescription";
import { useSwitchToSalesarea } from "../Vestigingen/UseSwitchToSalesarea";
import MealConfigurator from "./Tabs/MealConfigurator";
import { SalesareaProvider } from "../Upsell/modernTS/salesarea/SalesareaContext";

function ProductForm(props) {
  const endPoint = useMemo(() => {
    return "core/producten";
  }, []);

  const { currency, menukaartVestigingId, vestiging, extraFields, onGetModel } = useVestigingRelation({
    vestiging_id: props.vestiging_id,
    routerMatch: props.match,
    history: props.history,
    endPoint,
  });

  useSwitchToSalesarea(vestiging);

  const piggyEnabled = vestiging?.piggy?.use_loyalty;

  return (
    <AuthProvider prefix={"producten"}>
      <SalesareaProvider salesareaId={props.vestiging_id}>
        <CurrencyProvider currency={currency}>
          <VestigingProvider vestiging={vestiging}>
            <JamezzFormV2
              computedPageTitle={function (model) {
                return formatTitle(this.pageTitle, model.naam);
              }}
              customFieldSerialization={{
                eans: (eans) => {
                  return eans ? JSON.stringify(eans.map((eanObj) => eanObj.value)) : null;
                },
                child_option_groups: (child_option_groups) => JSON.stringify(child_option_groups),
              }}
              endPoint={endPoint}
              excludedKeysInOutput={{ group_id: true, vestiging: true, rawApiData: true }}
              extraFields={extraFields}
              extraFormProps={{
                vestiging,
                vestigingId: vestiging?.id ?? props.vestiging_id,
                menukaartVestigingId,
              }}
              mode={"onSubmit"}
              objectsExcludedAsRelation={{ opties: true, translations: true, omschrijving: true, rawApiData: true }}
              onGetModel={(model) => {
                if (model.proppos_product) {
                  Object.keys(model.proppos_product).forEach((key) => {
                    model["proppos_product__" + key] = model.proppos_product[key];
                  });
                }

                onGetModel(model);
              }}
              pageTitle={getTranslation("producten.form.pageTitle", "Product")}
              pageTitleSubject={
                <small>
                  <Link to={`/manager/vestigingen/${vestiging?.id}/edit`}>{vestiging?.naam}</Link> - {vestiging?.id}
                </small>
              }
              parseInput={{
                menukaart: (response) => (response ? JSON.stringify(response.map((menukaart) => menukaart.id)) : null),
                opties: (response) => (response ? JSON.stringify(response) : null),
                translations: (response) => (response ? parseJSONTryCatched(response) : null),
                rawApiData: (response) => (response ? parseJSONTryCatched(response) : null),
                prices: (response) => (response ? response : null),
                eans: (response) =>
                  response
                    ? parseJSONTryCatched(response)
                      ? JSON.parse(response).map((ean) => {
                          return { value: ean };
                        })
                      : []
                    : [],
                img_uuids: (response) => {
                  return response
                    ? JSON.parse(response).map((uuid) => ({
                        uuid: uuid,
                      }))
                    : [];
                },
                settings: (response) => {
                  return response ? JSON.parse(response) : null;
                },
                child_option_groups: (response) =>
                  response
                    ? response
                        .map((childOptionGroup) => ({
                          id: childOptionGroup.id,
                          name: childOptionGroup.name,
                          sort_key: childOptionGroup.sort_key,
                          pivot: childOptionGroup.pivot,
                          min_count: childOptionGroup.min_count,
                          max_count: childOptionGroup.max_count,
                        }))
                        .sort((a, b) => (a.pivot.sort_key ?? a.sort_key) - (b.pivot.sort_key ?? b.sort_key))
                    : null,
                custom_data: (response) => {
                  try {
                    return response ? JSON.parse(response) : {};
                  } catch (e) {
                    return {};
                  }
                },
              }}
              parseOutput={{
                translations: (translations) => (translations ? JSON.stringify(translations) : null),
                settings: (object) => JSON.stringify(object),
                custom_data: (o) => (typeof o !== "string" ? JSON.stringify(o) : o),
              }}
              revalidateMode={"onChange"}
              rootRight={"producten"}
              tabs={[
                {
                  right: "algemeen",
                  tabButton: <TranslationWrapper tDefault={"Algemeen"} tKey={"producten.form.tabs.algemeen"} />,
                  tabContent: <Algemeen />,
                },
                {
                  right: "afbeelding",
                  tabButton: (
                    <TranslationWrapper tDefault={"Branding / Design"} tKey={"vestigingen.form.tabs.branding"} />
                  ),
                  tabContent: <Branding />,
                },
                {
                  right: "vrijeopties",
                  tabButton: <TranslationWrapper tDefault={"Vrije opties"} tKey={"producten.form.tabs.vrijeOpties"} />,
                  tabContent: <Vrijeopties />,
                },
                {
                  right: "opties",
                  tabButton: <TranslationWrapper tDefault={"Opties"} tKey={"producten.form.tabs.opties"} />,
                  tabContent: <Opties />,
                },
                {
                  right: "optionGroups",
                  tabButton: (
                    <TranslationWrapper tDefault={"Option Groups"} tKey={"producten.form.tabs.optionGroups"} />
                  ),
                  tabContent: <ChildOptionGroups />,
                },
                {
                  right: "vertalingen",
                  tabButton: <TranslationWrapper tDefault={"Translations"} tKey={"translations"} />,
                  tabContent: <Translations />,
                },
                {
                  right: "vertalingen",
                  tabButton: <TranslationWrapper tDefault={"DetailedDescription"} tKey={"DetailedDescription"} />,
                  tabContent: <DetailedDescription />,
                },
                {
                  right: "timetable",
                  tabButton: <TranslationWrapper tDefault={"Tijd schema"} tKey={"producten.form.tabs.timetable"} />,
                  tabContent: <TimeTable />,
                },
                {
                  right: "informatie",
                  tabButton: <TranslationWrapper tDefault={"Informatie"} tKey={"producten.form.tabs.informatie"} />,
                  tabContent: <Informatie />,
                },
                {
                  right: "allergenen",
                  tabButton: <TranslationWrapper tDefault={"Allergenen"} tKey={"producten.form.tabs.allergenen"} />,
                  tabContent: <Allergenen />,
                },
                {
                  right: "combinatievoordeel",
                  tabButton: (
                    <TranslationWrapper
                      tDefault={"Combinatie voordeel"}
                      tKey={"producten.form.tabs.combinatieVoordeel"}
                    />
                  ),
                  tabContent: <Combinatievoordeel />,
                },
                {
                  right: "ayce",
                  tabButton: <TranslationWrapper tDefault={"All You Can Eat"} tKey={"producten.form.tabs.ayce"} />,
                  tabContent: <Ayce />,
                },
                {
                  right: "proppos",
                  tabButton: <TranslationWrapper tDefault={"Proppos"} tKey={"producten.form.tabs.proppos"} />,
                  tabContent: <Proppos />,
                },
                ...(piggyEnabled
                  ? [
                      {
                        right: "piggy",
                        tabButton: <TranslationWrapper tDefault={"Piggy Loyalty"} tKey={"producten.form.tabs.piggy"} />,
                        tabContent: <Piggy />,
                      },
                    ]
                  : []),
                {
                  right: "zones",
                  tabButton: <TranslationWrapper tDefault={"Zones"} tKey={"producten.form.tabs.zones"} />,
                  tabContent: <Zones />,
                },
                {
                  right: "tags",
                  tabButton: <TranslationWrapper tDefault={"Tags"} tKey={"producten.form.tabs.tags"} />,
                  tabContent: <Tags />,
                },
                {
                  right: "rawapidata",
                  tabButton: <TranslationWrapper tDefault={"Raw api data"} tKey={"producten.form.tabs.rawapidata"} />,
                  tabContent: <Apidata />,
                },
                {
                  right: "prices",
                  tabButton: <TranslationWrapper tDefault={"Product prices"} tKey={"producten.form.tabs.prices"} />,
                  tabContent: <Prices />,
                },
                {
                  right: "pause",
                  tabButton: (
                    <TranslationWrapper tDefault={"Pause product"} tKey={"producten.form.tabs.pause_product"} />
                  ),
                  tabContent: <Pause vestiging={vestiging} />,
                },
                {
                  right: "meal_configurator",
                  tabButton: (
                    <TranslationWrapper tDefault={"Meal configurator"} tKey={"producten.form.tabs.meal_configurator"} />
                  ),
                  tabContent: <MealConfigurator />,
                },
              ]}
            />
          </VestigingProvider>
        </CurrencyProvider>
      </SalesareaProvider>
    </AuthProvider>
  );
}

const mapStateToProps = (state) => {
  return {
    vestiging_id: state.filtersV2.vestiging_id?.value,
  };
};

export default connect(mapStateToProps)(ProductForm);
